<script setup lang="ts">
import { PV } from "@/widgets";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormMessage, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = boolean;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    hasValue?: () => boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: props.hasValue ?? (() => model.value !== undefined),
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <template v-if="!state.editMode">
            <div class="flex flex-row gap-2 items-center justify-center">
                <PV.ToggleSwitch v-model="model" class="min-w-10" disabled :inputId="state.id" />

                <div v-if="slots.icon !== undefined" class="flex flex-row gap-1 items-center">
                    <slot name="icon"></slot>
                    <label class="rns-label-style" :for="state.id">{{ props.label }}</label>
                </div>
                <template v-else>
                    <label class="rns-label-style" :for="state.id">{{ props.label }}</label>
                </template>

                <Buttons.BtnUpdate v-if="state.updatable" @click="actions.onUpdate" />
                <FormTool :actions="actions" :param="props" :state="state" />
            </div>
        </template>
        <template v-else>
            <div class="flex flex-row gap-2 items-center justify-center">
                <PV.ToggleSwitch
                    v-model="model"
                    class="min-w-10"
                    :disabled="props.disabled"
                    :form="props.formId"
                    :inputId="state.id"
                    :required="props.required"
                />

                <div v-if="slots.icon !== undefined" class="flex flex-row gap-1 items-center">
                    <slot name="icon"></slot>
                    <label class="rns-label-style hover:cursor-pointer" :for="state.id">{{ props.label }}</label>
                </div>
                <template v-else>
                    <label class="rns-label-style hover:cursor-pointer" :for="state.id">{{ props.label }}</label>
                </template>

                <FormTool :actions="actions" :param="props" :state="state" />
            </div>
        </template>

        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-label-style {
    @apply text-gray-600 font-semibold block;
}
</style>
